import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import Slider from 'rc-slider';
import position from './position.svg';
import styles from './VideosEditorSlider.module.scss';
import { useTextPanelContext } from '../../context/TextPanelContext';

const VideosEditorSlider = ({ scenes, selectedClip, playedSeconds, originalSceneIndex, handleSliderChange, updateTiming }) => {
  const [sliderPosition, setSliderPosition] = useState(0);
  const [marks, setMarks] = useState([]);
  const [marksObject, setMarksObject] = useState({});
  const [progressCoefficients, setProgressCoefficients] = useState(1);
  const [fullDuration, setFullDuration] = useState(0);
  const [sliderChange, setSliderChange] = useState(false);
  const throttleRef = useRef(null);
  const allDrafts = useMemo(() => scenes?.every(item => item.video === null), [scenes]);

  const scenesForSlider = useMemo(
    () =>
      scenes
        ? scenes.map(({ sort, trimEnd, trimStart, trimmedDuration, playbackRate = 1 }) => ({
            sort,
            trimEnd: Math.round(trimEnd / playbackRate),
            trimStart: Math.round(trimStart / playbackRate),
            trimmedDuration: Math.round(trimmedDuration / playbackRate),
            playbackRate,
          }))
        : scenes,
    [scenes]
  );

  const { isTextPanelEditor } = useTextPanelContext();

  const calculateProgressCoefficient = (index, marks) => {
    if (marks.length) {
      if (index === marks.length - 1) {
        return 1 - marks[index];
      } else {
        return marks[index + 1] - marks[index];
      }
    } else {
      return 1;
    }
  };

  const updateProgress = useCallback(
    (newSecondsPlayed, newClipIndex) => {
      newSecondsPlayed /= scenesForSlider[newClipIndex]?.playbackRate;
      if (scenesForSlider && scenesForSlider[newClipIndex]) {
        if (newSecondsPlayed <= scenesForSlider[newClipIndex].trimEnd) {
          const newCoefficient = calculateProgressCoefficient(newClipIndex, marks);
          const newSliderPosition =
            marks[newClipIndex] +
            newCoefficient *
              ((newSecondsPlayed * 1000 - scenesForSlider[newClipIndex].trimStart) / scenesForSlider[newClipIndex].trimmedDuration);
          newSliderPosition <= 1 && setSliderPosition(newSliderPosition);
          updateTiming({
            progress: Math.max(
              0,
              marks[newClipIndex] * fullDuration + (newSecondsPlayed - scenesForSlider[newClipIndex].trimStart / 1000)
            ),
          });
        }
      }
    },
    [setSliderPosition, updateTiming, fullDuration, marks, scenesForSlider, calculateProgressCoefficient]
  );

  const updateSliderMarks = () => {
    const scenesDurations = scenesForSlider.map(item => (item?.trimmedDuration ?? 0) / 1000);
    const marksSeconds = scenesDurations.map((_, index, array) => (index === 0 ? 0 : array.slice(0, index).reduce((i, j) => i + j)));
    const fullDuration = scenesDurations.reduce((prevValue, currValue) => prevValue + currValue, 0);
    setFullDuration(fullDuration);
    if (fullDuration > 0) {
      const marksPositions = marksSeconds.map(item => item / fullDuration);
      setMarks(marksPositions);
      const marksObjectForSlider = marksSeconds
        .slice(1)
        .map(item => item / fullDuration)
        .reduce((a, v) => ({ ...a, [`${v}`]: ' ' }), {});
      setMarksObject(marksObjectForSlider);
    } else {
      setMarks([]);
      setMarksObject({});
    }
  };

  const onSeekChange = useCallback(
    value => {
      if (!isTextPanelEditor && scenesForSlider) {
        // Define scene index
        const seekedVideoIndex = marks.filter(item => item <= value).length - 1;
        // Get updated coefficient to calculate seek to value in seconds
        const coef = calculateProgressCoefficient(seekedVideoIndex, marks);
        // Calculate seekTo position (from 0 to 1) for certain player from common slider position
        const seekToVideoCoefficient = (value - marks[seekedVideoIndex]) / coef;
        // Get seek to value for defined video
        const seekValue =
          (scenesForSlider[seekedVideoIndex]?.trimStart * (scenesForSlider[seekedVideoIndex]?.playbackRate ?? 1)) / 1000 +
          ((scenesForSlider[seekedVideoIndex]?.trimmedDuration * (scenesForSlider[seekedVideoIndex]?.playbackRate ?? 1)) / 1000) *
            seekToVideoCoefficient;
        setSliderChange(true);
        setTimeout(() => {
          setSliderChange(false);
        }, 10);
        handleSliderChange(originalSceneIndex ?? seekedVideoIndex, seekValue, updateProgress);
      }
    },
    [originalSceneIndex, marks, scenesForSlider, updateProgress]
  );

  useEffect(() => {
    if (scenesForSlider && scenesForSlider[selectedClip] && !sliderChange) {
      // Clear any existing throttle
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }

      // Throttle the slider position update to prevent excessive re-renders
      throttleRef.current = setTimeout(() => {
        if (playedSeconds <= scenesForSlider[selectedClip].trimEnd) {
          if (marks.length > 0) {
            const newSliderPosition =
              marks[selectedClip] +
              progressCoefficients[selectedClip] *
              ((playedSeconds * 1000 - scenesForSlider[selectedClip].trimStart) / scenesForSlider[selectedClip].trimmedDuration);
            if (newSliderPosition >= 0) {
              const roundedIsZero = Math.round(newSliderPosition * 1000) === 0;
              if (newSliderPosition <= 1) {
                setSliderPosition(roundedIsZero ? marks[selectedClip] : newSliderPosition);
              }
              updateTiming({
                progress: Math.max(
                  0,
                  marks[selectedClip] * fullDuration +
                  (roundedIsZero ? 0 : playedSeconds - scenesForSlider[selectedClip].trimStart / 1000)
                ),
              });
            } else {
              setSliderPosition(marks[selectedClip]);
            }
          }
        }
      }, 16); // ~60fps throttling
    }

    // Cleanup function
    return () => {
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }
    };
  }, [playedSeconds, selectedClip, scenesForSlider, sliderChange, marks, progressCoefficients, fullDuration, updateTiming]);

  useEffect(() => {
    const timing = { duration: fullDuration };
    if (scenes[selectedClip]?.trimStart) {
      timing.progress = Math.max(
        0,
        marks[selectedClip] * fullDuration +
          (playedSeconds - scenes[selectedClip].trimStart / (scenes[selectedClip].playbackRate || 1) / 1000)
      );
    }
    updateTiming(timing);
  }, [fullDuration]);

  useEffect(() => {
    // Update marks array if scenes changed
    if (scenesForSlider) {
      updateSliderMarks();
    }
  }, [scenesForSlider]);

  useEffect(() => {
    updateSliderMarks();
  }, []);

  useEffect(() => {
    if (scenesForSlider && !sliderChange) {
      const newSliderPosition =
        marks[selectedClip] +
        progressCoefficients[selectedClip] *
          ((playedSeconds * 1000 - scenesForSlider[selectedClip]?.trimStart) / scenesForSlider[selectedClip]?.trimmedDuration);
      setSliderPosition(Math.round(newSliderPosition * 1000) === 0 ? marks[selectedClip] : newSliderPosition);
    }
    // setSliderChange(false);
  }, [progressCoefficients, selectedClip]);

  useEffect(() => {
    if (!sliderChange) {
      // After clip was change from card list update slider position
      setSliderPosition(marks[selectedClip]);
    }
  }, [selectedClip]);

  useEffect(() => {
    setProgressCoefficients(scenesForSlider.map((_, index) => calculateProgressCoefficient(index, marks)));
  }, [marks]);

  return (
    <Slider
      min={0}
      max={1}
      marks={marksObject}
      className={styles.videoProgress}
      trackStyle={{
        backgroundColor: '#7C43AA',
        height: '8px',
        borderRadius: '4px 0 0 4px !important',
      }}
      handleStyle={{
        backgroundImage: `url(${position})`,
        backgroundPosition: '50% 0%',
        borderRadius: '0',
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
        cursor: 'default',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'transparent',
        zIndex: '51',
        height: '24px',
        width: '13px',
        opacity: allDrafts ? '0.5' : '1',
      }}
      railStyle={{
        height: '8px',
        backgroundColor: '#EEEEEE',
        opacity: allDrafts ? '0.5' : '1',
      }}
      step={0.001}
      onChange={onSeekChange}
      value={sliderPosition}
      included
      dotStyle={{
        width: '8px',
        height: '8px',
        backgroundColor: '#E1963E',
        border: 'none',
        borderRadius: 0,
        bottom: '0',
      }}
      disabled={allDrafts}
    />
  );
};

export default VideosEditorSlider;
